import { useState } from 'react';
import { Clock, X, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { useApp } from '../../context/AppContext';

function formatScheduledTime(isoStr: string): string {
    const date = new Date(isoStr);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);

    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `Today at ${timeStr}`;
    if (isTomorrow) return `Tomorrow at ${timeStr}`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ` at ${timeStr}`;
}

function truncate(str: string, n: number): string {
    return str.length > n ? str.slice(0, n) + '…' : str;
}

// Strip markdown formatting for preview
function plainTextPreview(content: string): string {
    return content
        .replace(/\*\*(.*?)\*\*/g, '$1')   // bold
        .replace(/\*(.*?)\*/g, '$1')         // italic
        .replace(/`(.*?)`/g, '$1')           // code
        .replace(/#+\s/g, '')                // headings
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
        .replace(/\n+/g, ' ')
        .trim();
}

export default function ScheduledMessagesPanel() {
    const { scheduledMessages, cancelScheduledMessage } = useApp();
    const [expanded, setExpanded] = useState(true);
    const [cancelling, setCancelling] = useState<string | null>(null);

    if (scheduledMessages.length === 0) return null;

    const handleCancel = async (messageId: string) => {
        setCancelling(messageId);
        try {
            await cancelScheduledMessage(messageId);
        } catch (err) {
            console.error('Failed to cancel scheduled message:', err);
        } finally {
            setCancelling(null);
        }
    };

    return (
        <div style={{
            borderTop: '1px solid var(--border-color)',
            background: 'rgba(245, 158, 11, 0.04)',
        }}>
            {/* Header */}
            <button
                type="button"
                onClick={() => setExpanded(e => !e)}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: 'left',
                    transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(245, 158, 11, 0.08)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
                <Clock size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                <span style={{ color: '#f59e0b' }}>
                    {scheduledMessages.length} scheduled {scheduledMessages.length === 1 ? 'message' : 'messages'}
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>
                    {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </span>
            </button>

            {/* Message list */}
            {expanded && (
                <div style={{ padding: '0 12px 8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {scheduledMessages.map(msg => (
                        <div
                            key={msg.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '7px 12px',
                                borderRadius: 8,
                                border: '1px solid rgba(245, 158, 11, 0.25)',
                                background: 'rgba(245, 158, 11, 0.06)',
                            }}
                        >
                            <Calendar size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                                    {truncate(plainTextPreview(msg.content), 60)}
                                </span>
                                {msg.scheduled_at && (
                                    <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 1 }}>
                                        {formatScheduledTime(msg.scheduled_at)}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => handleCancel(msg.id)}
                                disabled={cancelling === msg.id}
                                title="Cancel scheduled message"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: cancelling === msg.id ? 'not-allowed' : 'pointer',
                                    color: 'var(--text-muted)',
                                    padding: 4,
                                    borderRadius: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    opacity: cancelling === msg.id ? 0.4 : 1,
                                    transition: 'color 0.15s, background 0.15s',
                                }}
                                onMouseEnter={e => { if (cancelling !== msg.id) { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; } }}
                                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
